import { Component, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentSize } from '../types';

export interface TableColumn {
  field: string;
  header: string;
  sortable?: boolean;
  width?: string;
}

export interface TableRow {
  [key: string]: any;
}

@Component({
  selector: 'app-data-table',
  imports: [CommonModule],
  template: `
    <div class="overflow-x-auto">
      <table [class]="tableClasses">
        <thead>
          <tr>
            @for (col of columns(); track col.field) {
              <th [class]="getHeaderClasses(col)" [style.width]="col.width">
                <div class="flex items-center gap-2">
                  <span>{{ col.header }}</span>
                  @if (col.sortable) {
                    <button
                      type="button"
                      (click)="sort(col.field)"
                      class="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </button>
                  }
                </div>
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of sortedData(); track $index) {
            <tr [class]="getRowClasses()">
              @for (col of columns(); track col.field) {
                <td [class]="getCellClasses()">
                  {{ row[col.field] }}
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
  styles: []
})
export class DataTableComponent {
  columns = input<TableColumn[]>([]);
  data = input<TableRow[]>([]);
  size = input<ComponentSize>('md');
  striped = input<boolean>(false);
  hoverable = input<boolean>(true);

  sortField = signal<string>('');
  sortOrder = signal<'asc' | 'desc'>('asc');

  sortedData = computed(() => {
    const field = this.sortField();
    if (!field) return this.data();
    
    const sorted = [...this.data()].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      if (aVal < bVal) return this.sortOrder() === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortOrder() === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  });

  sort(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.update(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortOrder.set('asc');
    }
  }

  get tableClasses(): string {
    return 'min-w-full divide-y divide-gray-200 dark:divide-gray-700';
  }

  getHeaderClasses(col: TableColumn): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    return `px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 ${sizeMap[this.size()]}`;
  }

  getRowClasses(): string {
    const classes = ['bg-white', 'dark:bg-gray-800'];
    if (this.striped()) {
      classes.push('even:bg-gray-50', 'dark:even:bg-gray-800/50');
    }
    if (this.hoverable()) {
      classes.push('hover:bg-gray-50', 'dark:hover:bg-gray-700');
    }
    return classes.join(' ');
  }

  getCellClasses(): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    return `px-4 py-3 whitespace-nowrap text-gray-900 dark:text-gray-100 ${sizeMap[this.size()]}`;
  }
}

